import { useIfpFormContext } from '@sec-loans-ui/ifp-loans/steps/formContext'
import { IfpWizardType, WizardStep } from '@sec-loans-ui/ifp-loans/wizard/wizardMachine'
import { ImplicitlySubmittableForm } from '@ui-common/components/ImplicitlySubmittableForm'
import { FullPagePhases, Phase, Phases } from '@ui-common/components/Phases'
import { useWizardForm } from '@ui-common/hooks'
import { useInView } from '@ui-common/hooks/useInView'
import { getFullname } from '@ui-common/utils'
import { Divider } from '@ui-components/decorative/Divider'
import { FlexColumnContainer } from '@ui-components/global/CommonStyling'
import { LogoIconType } from '@ui-components/global/ComponentProps'
import { PAGE_COMPONENT_GAP } from '@ui-components/global/constants'
import { WizardStep as WizardStepComponent } from '@ui-components/layout/WizardStep'
import { ConfirmButtonProps } from '@ui-components/layout/WizardTypes'
import { SecondaryHeading } from '@ui-components/typography/headings/SecondaryHeading'
import { Fragment, useEffect } from 'react'
import { FormProvider } from 'react-hook-form'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { useIfpAnalyticsService, useIfpPageViewEvent } from '../../analytics/analyticsContext'
import { useSubHeaderOptionsWithMenu } from '../../hooks/useSubHeaderOptionsWithMenu'
import { ApplicantInfoSummary } from '../applicant-info/ApplicantInfoSummary'
import { HouseholdInfoSummary } from '../household-info/HouseholdInfoSummary'
import { IfpInfoSummary } from '../ifp-info/IfpInfoSummary'
import { useStatusStepLogic } from './useStatusStepLogic'

export const StatusStep = ({
  onGoToApplicationsClick,
  onNewApplicationClick,
}: {
  onGoToApplicationsClick: () => void
  onNewApplicationClick: () => void
}) => {
  const intl = useIntl()
  const {
    wizardData,
    exitWithConfirmDialog,
    user,
    proceed,
    onEdit,
    onEditApplicantInfo,
    wizardType,
    isFinalizationPhase,
    isWizardComplete,
    hasSendError,
    showSendErrorModal,
    isPrimaryApplicant,
    step,
  } = useIfpFormContext()
  const isSecondaryApplicant = !isPrimaryApplicant
  const isLongWizardType = wizardType === IfpWizardType.Long
  const methods = useWizardForm({
    defaultValues: wizardData,
  })

  const { analyticsService } = useIfpAnalyticsService()

  const statusStepTitle = intl.formatMessage({ id: 'ifp-application-top-bar-heading' })

  useIfpPageViewEvent(step)

  const shouldShowSummary = !!wizardData?.basicInfo
  const PhasesView = shouldShowSummary ? Phases : FullPagePhases
  const shouldShowHouseholdInfoSummary = (isLongWizardType && isFinalizationPhase) || !!wizardData?.householdInfo
  const { proceedToConsent, openReturnApplicationModal } = useStatusStepLogic()

  const subHeaderOptions = useSubHeaderOptionsWithMenu({
    onGoToApplicationsClick,
    onNewApplicationClick,
  })
  useEffect(() => {
    if (hasSendError) {
      showSendErrorModal()
    }
  }, [hasSendError, showSendErrorModal])

  const [summaryHeadingRef, isSummaryHeadingInView] = useInView({ initialInView: true, rootMargin: '-100px' })
  // Use 200px margin to not show floating action bar when there is only a little bit of scrolling
  const [confirmButtonRef, isConfirmButtonInView] = useInView({ rootMargin: '200px' })
  const arePhasesAndConfirmButtonOutOfView = !isConfirmButtonInView && !isSummaryHeadingInView

  const confirmButtonProps: ConfirmButtonProps | undefined = isWizardComplete
    ? {
        onConfirm: () => {
          analyticsService.sendWizardStepCustomEvent(step, 'ContinueButton')
          proceedToConsent()
        },
        confirmLabel: intl.formatMessage({ id: 'common-continue' }),
        shouldShowConfirmFloating: arePhasesAndConfirmButtonOutOfView,
      }
    : undefined

  const secondaryButtonProps: ConfirmButtonProps | undefined =
    isWizardComplete && isSecondaryApplicant
      ? {
          confirmLabel: intl.formatMessage({ id: 'ifp-application-secondary-user-edit-button-label' }),
          onConfirm: () => {
            analyticsService.sendWizardStepCustomEvent(step, 'ReturnApplicationButton')
            openReturnApplicationModal()
          },
        }
      : undefined

  const phases: Phase[] = [
    {
      header: intl.formatMessage({ id: 'ifp-application-phases-info-phase-heading' }),
      content: {
        icon: 'controlEditIcon',
        text: intl.formatMessage({ id: 'ifp-application-phases-info-phase-description' }),
        action: {
          onClick: () => proceed({}),
        },
      },
      isComplete: !!isFinalizationPhase,
    },
    {
      header: intl.formatMessage({ id: 'sec-loans-phases-finalization-phase-header' }),
      content: {
        text: intl.formatMessage({ id: 'ifp-application-finalization-phase-description' }),
      },
      isComplete: false,
      isFinalizationPhase: true,
    },
  ]

  return (
    <WizardStepComponent
      stepId={WizardStep.StatusStep}
      isLoading={false}
      title={statusStepTitle}
      headerButtonProps={{
        headerButtonLabel: intl.formatMessage({ id: 'common-exit' }),
        onHeaderButtonClick: exitWithConfirmDialog,
      }}
      confirmButtonProps={confirmButtonProps}
      confirmButtonRef={confirmButtonRef}
      secondaryButtonProps={secondaryButtonProps}
      additionalText={getFullname({ firstName: user?.firstNames, lastName: user?.lastName })}
      subHeaderOptions={subHeaderOptions}
      icon={LogoIconType.SPankki}
      shouldShowMainHeader={true}
      pageMessage={
        !isPrimaryApplicant
          ? intl.formatMessage({
              id: 'ifp-application-status-secondary-application-info',
            })
          : undefined
      }
      ctaHeadingProps={
        !isPrimaryApplicant
          ? {
              header: intl.formatMessage({ id: 'ifp-application-confirm-application-section-header' }),
              additionalText: intl.formatMessage({ id: 'ifp-application-confirm-application-section-text' }),
            }
          : undefined
      }
      pageHeroContent={
        isPrimaryApplicant && (
          <PhasesView
            isSummaryLikeView={true}
            phases={phases}
            instructionOverrides={{
              initial: {
                title: intl.formatMessage({ id: 'ifp-application-phases-not-started-title' }),
                text: intl.formatMessage({ id: 'ifp-application-phases-not-started-description' }),
              },
            }}
          />
        )
      }
    >
      {shouldShowSummary && (
        <FormProvider {...methods}>
          <ImplicitlySubmittableForm onSubmit={() => methods.handleSubmit} isLoading={false}>
            <SummaryContainer>
              <SecondaryHeading
                ref={summaryHeadingRef}
                header={intl.formatMessage({ id: 'common-status-summary-header' })}
                headerCaption={intl.formatMessage({ id: 'ifp-application-summary-header-caption' })}
              />
              {wizardData.basicInfo && (
                <IfpInfoSummary
                  data={wizardData.basicInfo}
                  onEditRequest={isPrimaryApplicant ? () => onEdit(WizardStep.IfpInfoStep) : undefined}
                />
              )}
              {shouldShowHouseholdInfoSummary && (
                <>
                  <Divider />
                  <HouseholdInfoSummary
                    data={wizardData.householdInfo}
                    onEditRequest={isPrimaryApplicant ? () => onEdit(WizardStep.HouseholdInfoStep) : undefined}
                  />
                </>
              )}
              {wizardData.applicantInfos?.map((applicantInfo, i, applicantInfos) => (
                <Fragment key={i}>
                  <Divider />
                  <ApplicantInfoSummary
                    key={i}
                    index={i}
                    data={applicantInfo}
                    numberOfApplicants={applicantInfos.length}
                    onEditRequest={isPrimaryApplicant ? (index) => onEditApplicantInfo(index) : undefined}
                  />
                </Fragment>
              ))}
            </SummaryContainer>
          </ImplicitlySubmittableForm>
        </FormProvider>
      )}
    </WizardStepComponent>
  )
}

const SummaryContainer = styled(FlexColumnContainer)`
  gap: ${PAGE_COMPONENT_GAP}px;
  padding-bottom: ${PAGE_COMPONENT_GAP}px;
`
